// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useNavigate, useLocation } from "react-router-dom"
import {
  RefreshCw,
  LayoutGrid,
  Bell,
  User as UserIcon,
  LogOut,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react"

import ebioseMark from "@assets/brand/ebiose_mark_mint.svg"
import { useUserSelf } from "@/queries/queries.users"
import { useLogout } from "@/queries/session"
import { useAllEvolutionCycles } from "@/queries/queries.evolutionCycles"
import { formatCurrency } from "@/utils"
import { cn } from "@sglara/cn"

// In-between shades not in the Lagon token set use exact hex values from the mockup.

type NavEntry = {
  icon: LucideIcon
  label: string
  path: string
}

const NAV: NavEntry[] = [
  { icon: RefreshCw, label: "My Cycles", path: "/evolution-cycles" },
  { icon: LayoutGrid, label: "My Forges", path: "/forges" },
  { icon: UserIcon, label: "My Account", path: "/profile" },
]

// Rendered only for `User.Role === "ADMIN"`. A non-admin gets no nav
// entry as well as no data if they navigate to the route directly
// (gated in the view).
const ADMIN_NAV: NavEntry = {
  icon: ShieldCheck,
  label: "Credit requests",
  path: "/admin/credit-requests",
}

export const LeftNav = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user } = useUserSelf()
  const logout = useLogout()

  return (
    <aside
      className="sticky top-0 self-start h-screen flex-[0_0_232px] box-border flex flex-col gap-[3px] px-[13px] py-[18px] border-r border-border"
      style={{
        background:
          "linear-gradient(180deg, rgba(8,33,38,0.92), rgba(4,18,22,0.94))",
      }}
    >
      {/* brand lockup */}
      <div
        className="flex items-center gap-[9px] px-1 pt-1 pb-[18px] cursor-pointer"
        onClick={() => navigate("/")}
      >
        <img
          src={ebioseMark}
          alt="Ebiose"
          className="h-[39px] block"
          style={{ filter: "drop-shadow(0 0 8px rgba(69,240,200,0.55))" }}
        />
        <span
          className="text-[24px] font-semibold text-foreground leading-none tracking-[-0.01em]"
          style={{ textShadow: "0 0 14px rgba(69,240,200,0.25)" }}
        >
          ebiose
          <sup
            className="text-[12px] font-bold text-primary align-super tracking-[0.04em] ml-px"
            style={{ textShadow: "0 0 8px rgba(69,240,200,0.7)" }}
          >
            AI
          </sup>
        </span>
      </div>

      <span className="text-[10px] uppercase tracking-[0.12em] text-[#3E5E59] px-[10px] pt-[6px] pb-1">
        Workspace
      </span>

      {NAV.map((entry) => (
        <NavItem
          key={entry.path}
          {...entry}
          active={location.pathname.startsWith(entry.path)}
          onClick={() => navigate(entry.path)}
        />
      ))}
      {user?.role === "ADMIN" && (
        <NavItem
          {...ADMIN_NAV}
          active={location.pathname.startsWith(ADMIN_NAV.path)}
          onClick={() => navigate(ADMIN_NAV.path)}
        />
      )}

      <NotificationsItem />

      <div className="mt-auto flex flex-col gap-[10px]">
        <CycleStatusPanel />
        <div className="h-px bg-border mx-[6px]" />
        <div
          className="flex items-center gap-[11px] px-[11px] py-[9px] rounded-[10px] text-[13px] text-[#FF8A7E] cursor-pointer transition-colors hover:bg-[rgba(255,138,126,0.08)]"
          onClick={() => logout.mutate()}
        >
          <LogOut size={17} strokeWidth={2} />
          Logout
        </div>
      </div>
    </aside>
  )
}

const NavItem = ({
  icon: Icon,
  label,
  active,
  onClick,
}: NavEntry & { active: boolean; onClick: () => void }) => {
  if (active) {
    return (
      <div
        onClick={onClick}
        className="relative flex items-center gap-[11px] px-[11px] py-[9px] rounded-[10px] text-[13px] font-medium text-foreground border border-primary/45 cursor-pointer"
        style={{
          background:
            "linear-gradient(100deg, rgba(69,240,200,0.2), rgba(69,240,200,0.05))",
        }}
      >
        <span
          className="absolute left-[-14px] top-1/2 -translate-y-1/2 w-[3px] h-[20px] rounded-[3px] bg-primary"
          style={{ boxShadow: "0 0 10px #45F0C8" }}
        />
        <Icon size={17} strokeWidth={2} className="text-primary" />
        {label}
      </div>
    )
  }
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-[11px] px-[11px] py-[9px] rounded-[10px] text-[13px] text-[#5F8C84] cursor-pointer transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-[#D9F2EA]"
    >
      <Icon size={17} strokeWidth={2} />
      {label}
    </div>
  )
}

// No notifications backend wired yet; badge logic is present but count stays 0.
const NotificationsItem = () => {
  // No notifications source wired yet — render the entry without a badge.
  const count = 0
  return (
    <div className="flex items-center gap-[11px] px-[11px] py-[9px] rounded-[10px] text-[13px] text-[#5F8C84] cursor-pointer transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-[#D9F2EA]">
      <Bell size={17} strokeWidth={2} />
      <span>Notifications</span>
      {count > 0 && (
        <span className="ml-auto inline-flex items-center justify-center min-w-[17px] h-[17px] px-[5px] box-border rounded-full bg-primary/15 text-primary text-[10px] font-bold font-mono border border-primary/35">
          {count}
        </span>
      )}
    </div>
  )
}

const STATUS_DOT: Record<string, string> = {
  running: "var(--running)",
  completed: "var(--done)",
  failed: "var(--failed)",
  expired: "var(--expired)",
}

const CycleStatusPanel = () => {
  const { data: user } = useUserSelf()
  const { data: cycles } = useAllEvolutionCycles()
  const latest = cycles?.items?.[0]

  if (!latest && user?.credits == null) return null

  const status = latest?.status ?? "running"
  const dot = STATUS_DOT[status] ?? "var(--running)"

  return (
    <div className="flex flex-col gap-[7px] px-2 pt-[10px] pb-1">
      {latest && (
        <div className="flex items-center gap-2">
          <span
            className={cn("w-[7px] h-[7px] rounded-full shrink-0", {
              "animate-pulse-dot": status === "running",
            })}
            style={{ background: dot }}
          />
          <span className="text-[12.5px] font-medium text-[#D9F2EA] capitalize">
            {status}
          </span>
          <span className="ml-auto font-mono text-[11px] text-[#5F8C84]">
            {latest.n_candidates} agents
          </span>
        </div>
      )}
      {user?.credits != null && (
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.05em] text-[#4E6E69]">
            credits
          </span>
          <span className="font-mono text-[11px] text-[#6E8C86]">
            {formatCurrency(user.credits, "USD")}
          </span>
        </div>
      )}
    </div>
  )
}
